// src/hooks/useQuiz.ts
import useSWR, { mutate } from 'swr';
import { fetchQuizById } from '../services/quizService';
// MOD: IndexedDBヘルパーをインポート
import { getQuizFromDb, setQuizToDb, deleteQuizFromDb } from '../lib/quizIdbHelper';
import { Quiz } from '../types';
import { logger } from '../utils/logger';

/**
 * SWRのfetcher関数。オフラインファーストで動作する。
 * 1. IndexedDBからキャッシュを取得
 * 2. キャッシュがなければFirestoreから取得
 * 3. Firestoreから取得したデータをIndexedDBに保存
 * @param quizId - 取得するクイズのID
 * @returns クイズデータ
 */
const quizFetcher = async (quizId: string): Promise<Quiz | null> => {
    // 1. まずIndexedDBからキャッシュを取得
    const cachedQuiz = await getQuizFromDb(quizId);
    if (cachedQuiz) {
        logger.log(`Quiz ${quizId} loaded from IndexedDB cache.`);
        return cachedQuiz;
    }

    // 2. オフラインでキャッシュもない場合は、これ以上進めない
    if (!navigator.onLine) {
        logger.warn(`Offline and no cache available for quiz ${quizId}.`);
        return null;
    }

    // 3. オンラインでキャッシュがない場合はFirestoreから取得
    logger.log(`Fetching quiz ${quizId} from Firestore.`);
    const quiz = await fetchQuizById(quizId);

    // 4. Firestoreから取得したデータをIndexedDBキャッシュに保存
    if (quiz) {
        await setQuizToDb(quiz);
    }
    return quiz;
};

/**
 * 特定のクイズデータを取得するためのSWRフック
 * @param quizId - 取得するクイズのID
 * @returns SWRのデータ、エラー、ローディング状態、そしてキャッシュを更新する関数
 */
export const useQuiz = (quizId: string) => {
    // quizIdが存在する場合のみfetcherを実行
    const { data, error, isLoading } = useSWR<Quiz | null>(quizId ? quizId : null, quizFetcher, {
        revalidateOnFocus: false, // フォーカス時に再検証しない
        revalidateIfStale: false, // 古いデータがあっても再検証しない
        shouldRetryOnError: false, // エラー時のリトライはしない（オフラインを考慮）
    });

    /**
     * SWRのメモリキャッシュとIndexedDBのキャッシュの両方を最新データで更新する。
     * @param newData 更新する新しいクイズデータ
     */
    const updateQuizAndLocalCache = async (newData: Quiz) => {
        if (!quizId) {
            logger.warn("updateQuizAndLocalCache called with no quizId.");
            return;
        }
        // SWRのメモリキャッシュを直接更新（再検証はしない）
        mutate(quizId, newData, { revalidate: false });
        // IndexedDBのキャッシュも更新
        await setQuizToDb(newData);
        logger.log(`Quiz ${quizId} updated in both SWR and IndexedDB cache.`);
    };

    return {
        quiz: data,
        isLoading,
        isError: error,
        // SWRのキャッシュを更新する標準的なmutate
        mutateQuiz: (newData?: Quiz | Promise<Quiz | null> | null, shouldRevalidate?: boolean) => mutate(quizId, newData, shouldRevalidate),
        // SWRとIndexedDBの両方を更新するカスタム関数
        updateQuizAndLocalCache: updateQuizAndLocalCache,
    };
};

/**
 * 特定のクイズデータをキャッシュから削除し、SWRキャッシュも無効化する
 * @param {string} quizId - 削除するクイズのID
 */
export const invalidateQuizCache = async (quizId: string) => {
    // IndexedDBから削除
    await deleteQuizFromDb(quizId);
    // SWRキャッシュも無効化し、次回取得時に再検証させる
    mutate(quizId, undefined, { revalidate: true });
    logger.log(`Quiz ${quizId} removed from IndexedDB cache and SWR cache invalidated.`);
};

/**
 * クイズデータをキャッシュから、またはFirestoreから取得する関数
 * useQuizフック外から直接キャッシュを読んだり、Firestoreから取得するために使用
 * @param quizId - 取得するクイズのID
 * @returns クイズデータ
 */
export const getQuizDataWithCache = async (quizId: string): Promise<Quiz | null> => {
    // オフライン/オンライン対応のquizFetcherロジックをそのまま再利用
    return quizFetcher(quizId);
};